import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PendingSendContext = createContext(null);

export const usePendingSends = () => {
  const context = useContext(PendingSendContext);
  if (!context) throw new Error('usePendingSends must be used inside PendingSendProvider');
  return context;
};

export const PendingSendProvider = ({ enabled, children }) => {
  const [pendingSends, setPendingSends] = useState([]);
  const [undoingIds, setUndoingIds] = useState([]);
  const pendingRef = useRef([]);
  const refreshingRef = useRef(false);
  const locallyUndoneRef = useRef(new Set());

  const notifySettled = useCallback(async (item) => {
    if (locallyUndoneRef.current.delete(item.id)) return;
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/pending-sends/${item.id}`);
      if (!response.ok) return;
      const detail = await response.json();
      window.dispatchEvent(new CustomEvent('ps-send-settled', { detail }));
      if (detail.status === 'sent') {
        const acceptedAt = detail.smtp_accepted_at ? new Date(detail.smtp_accepted_at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '';
        toast.success(`SMTP accepted the reply to ${detail.recipients?.[0] || 'recipient'}${acceptedAt ? ` at ${acceptedAt}` : ''}`);
      }
      if (detail.status === 'failed') toast.error('Reply could not be sent and was restored to Drafts');
    } catch (_error) {
      // The Inbox refresh remains the source of truth if this status lookup fails.
    }
  }, []);

  const refreshPendingSends = useCallback(async () => {
    if (!enabled || refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/pending-sends`);
      if (!response.ok) return;
      const next = await response.json();
      const nextIds = new Set(next.map(item => item.id));
      pendingRef.current.filter(item => !nextIds.has(item.id)).forEach(notifySettled);
      pendingRef.current = next;
      setPendingSends(next);
    } finally {
      refreshingRef.current = false;
    }
  }, [enabled, notifySettled]);

  useEffect(() => {
    if (!enabled) {
      pendingRef.current = [];
      setPendingSends([]);
      return undefined;
    }
    refreshPendingSends();
    const interval = window.setInterval(refreshPendingSends, 500);
    return () => window.clearInterval(interval);
  }, [enabled, refreshPendingSends]);

  const queuePendingSend = useCallback((item) => {
    setPendingSends(current => {
      const next = [...current.filter(existing => existing.id !== item.id), item];
      pendingRef.current = next;
      return next;
    });
  }, []);

  const undoPendingSend = useCallback(async (item) => {
    setUndoingIds(current => [...current, item.id]);
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/pending-sends/${item.id}/undo`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'The undo window has closed');
      locallyUndoneRef.current.add(item.id);
      setPendingSends(current => {
        const next = current.filter(existing => existing.id !== item.id);
        pendingRef.current = next;
        return next;
      });
      window.dispatchEvent(new CustomEvent('ps-send-undone', { detail: data }));
      toast.success('Send undone. Your reply is back in Drafts.');
      return true;
    } catch (error) {
      toast.error(error.message || 'Could not undo this send');
      await refreshPendingSends();
      return false;
    } finally {
      setUndoingIds(current => current.filter(id => id !== item.id));
    }
  }, [refreshPendingSends]);

  return (
    <PendingSendContext.Provider value={{ pendingSends, queuePendingSend, undoPendingSend, undoingIds }}>
      {children}
    </PendingSendContext.Provider>
  );
};